import { Tag } from 'antd';
import { isNil, startCase as _startCase } from 'lodash';
import { useCallback } from 'react';
import { useTheme } from 'styled-components';

import {
  type AtlasGqlInspectionDeliveryStatus,
  type AtlasGqlTaskStatus,
  AtlasGqlInspectionStatus,
} from 'types/atlas-graphql';
import { type HorizonTheme } from 'utils/color/horizonTheme';

export function getSeverityColor(
  theme: HorizonTheme,
  severity?: number | null,
  maxValue: number = 5
) {
  if (isNil(severity)) {
    return null;
  }
  if (severity >= maxValue) {
    return theme.severityMaxColor;
  }
  if (severity >= maxValue - 1) {
    return theme.severityHighColor;
  }
}

export const StatusTag: React.FC<{
  status: AtlasGqlTaskStatus | AtlasGqlInspectionStatus | AtlasGqlInspectionDeliveryStatus;
  startCase?: boolean;
}> = ({ status, startCase = true }) => {
  const theme = useTheme();

  const getStatusColor = useCallback(
    (status: AtlasGqlTaskStatus | AtlasGqlInspectionStatus | AtlasGqlInspectionDeliveryStatus) => {
      switch (status) {
        case 'IN_PROGRESS':
        case AtlasGqlInspectionStatus.AnalysisPending:
        case AtlasGqlInspectionStatus.AnalysisInProgress:
          return theme.teal;
        case 'PENDING':
        case 'DRAFT':
        case AtlasGqlInspectionStatus.DataCapturePending:
        case AtlasGqlInspectionStatus.DataCaptureInProgress:
          return theme.defaultStatusColor;
        case 'COMPLETE':
        case 'COMPLETED':
        case 'RELEASED':
        case AtlasGqlInspectionStatus.AnalysisComplete:
          return theme.green;
        case 'FAILED':
          return theme.red;
        default:
          return undefined;
      }
    },
    [status, theme]
  );

  return (
    <Tag color={getStatusColor(status)}>
      {startCase ? _startCase(status.toLowerCase()) : status}
    </Tag>
  );
};

export type AssetColorStatus =
  | 'Active'
  | 'Curtailed'
  | 'Decommissioned'
  | 'Offline'
  | 'Online'
  | 'Recycled'
  | 'Storage';

export function getAssetColorStatus(status: AssetColorStatus, theme: HorizonTheme) {
  switch (status) {
    case 'Offline':
    case 'Recycled':
      return theme.red;
    case 'Online':
    case 'Active':
      return theme.teal;
    case 'Curtailed':
    case 'Storage':
      return theme.severityHighColor;
    case 'Decommissioned':
      return theme.secondaryBackgroundColor;
    default:
      return theme.secondaryBackgroundColor;
  }
}

export const getOpSeverityColor = (
  theme: HorizonTheme,
  opSeverity?: number
): string | undefined => {
  if (isNil(opSeverity) || !Number.isFinite(opSeverity)) {
    return undefined;
  }

  // 1.0 - 3.9: use default text color
  if (opSeverity <= 3.9) {
    return undefined;
  }

  if (opSeverity <= 4.4) {
    return theme.opSeverity.pureOrange;
  }

  if (opSeverity <= 4.8) {
    return theme.opSeverity.deepOrange;
  }

  if (opSeverity <= 4.9) {
    return theme.opSeverity.darkBurntOrange;
  }

  if (opSeverity <= 5.4) {
    return theme.opSeverity.redAnchor;
  }

  if (opSeverity <= 5.8) {
    return theme.opSeverity.crimson;
  }

  return theme.opSeverity.bloodRed;
};
